import { useState, useEffect, useRef, useMemo } from 'react';
import { sandhillsRequest, repositionCookieBanner, getURLParameter, insertURLParameter, removeURLParameter, setScrollTop, setIOSScrollLock, removeIOSScrollLock, syncCanonicalUrl } from '@Components/Utilities/SandhillsUtility';
import { getUserPosition } from '@Components/Utilities/Geolocation';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import FacetedSearchMobileMenu from '@Components/Shared/FacetedSearch/FacetedSearchMobileMenu';
import Paging from '@Components/MuiWrappers/Paging';
import SimpleHomeBreadcrumb from '@Components/Shared/SimpleHomeBreadcrumb';
import TranslationText from '@Components/Utilities/TranslationText';
import SortDropdown from '@Components/Shared/SortDropdown';
import FacetedSearch from '@Components/Shared/FacetedSearch/FacetedSearch';
import DealerDirectoryListing from '@Components/DealerDirectory/DealerDirectoryListing';
import FBODirectoryListing from '@Components/DealerDirectory/FBODirectoryListing';
import CategoryInformation from '@Components/Shared/CategoryInformation';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { togglePushNotifications } from '@Components/Shared/PushNotificationButton';
import { toggleScrollToTop } from '@Components/Shared/ScrollToTop';
import ready from 'document-ready';
import { queryString } from '@Components/Utilities/SandhillsConstants';
import LocationMap from '@Components/Maps/LocationMap';
import debounce from 'lodash.debounce';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';


const EmailModal = loadable(() => import('@Components/Shared/Modals/EmailModal'), true);
const DistanceLocationModal = loadable(() => import('@Components/Shared/Modals/DistanceLocationModal'), true);
const SEOLinks = loadable(() => import('@Components/Listings/SEOLinks'));

const DealerDirectoryView = (props) => {

    const currentUser = useRecoilValue(atoms.currentUser);
    const constants = {
        AirportDetails: 'AirportDetails',
        AirportID: 'AirportID',
        Bounds: 'Bounds',
        CityID: 'CityID',
        Distance: 'Distance',
        sort: 'Sort',
        UseCurrentLocation: 'UseCurrentLocation'
    };
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);

    const [searchData, setSearchData] = useState({
        SearchBaseURL: props.DealerDirectoryURL,
        Dealers: props.Dealers,
        SearchFields: props.SearchFields,
        ListingsCount: props.PaginationModel.TotalListingsCountFormatted,
        PageHeader: props.PageHeader,
        CurrentPage: props.PaginationModel.CurrentPage ? props.PaginationModel.CurrentPage : 1,
        TotalPages: props.PaginationModel.TotalPages,
        InternalLinkingModel: props.InternalLinkingModel,
        SelectedSortValue: props.SelectedSortValue,
        IsDefaultSearch: props.IsDefaultSearch,
        CategoryInfo: props.CategoryInformation,
        LocationString: props.LocationString,
        ListingLocations: props.ListingLocations,
        ListingMapData: props.ListingMapData,
        DefaultZoom: props.DefaultZoom,
        DefaultCenter: props.DefaultCenter,
        FlightPaths: props.FlightPaths
    });

    const windowSize = useWindowSize();
    const [isMounted, setIsMounted] = useState(false);
    const [dealerDirectorySearchURL, setDealerDirectorySearchURL] = useState(props.DealerDirectorySearchURL);
    const [showSearchWindow, setShowSearchWindow] = useState(false);
    const [mobileSearch, setMobileSearch] = useState(false);
    const [searchInProgress, setSearchInProgress] = useState(false);
    const [outstandingChanges, setOutstandingChanges] = useState([]);
    const [showDistanceModal, setShowDistanceModal] = useState(false);
    const [samModel, setSamModel] = useState(props.SAMModel);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState('');
    const [showMobileSort, setShowMobileSort] = useState(false);
    const [returnFocusRef, setReturnFocusRef] = useState({});
    const [useCurrentLocation, setUseCurrentLocation] = useState(false);
    const locationMapRef = useRef(null);
    const [showMapViewModal, setShowMapViewModal] = useState(false);
    const [locationState, setLocationState] = useState(null);
    const [showLoadingRing, setShowLoadingRing] = useState(false);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll, true);
        const cookieBannerResize = () => {
            repositionCookieBanner('mobile-option-bar', 786);
        };
        EmailModal.preload();
        ready(() => {
            cookieBannerResize();
            window.addEventListener('resize', cookieBannerResize);
            if (window.outerWidth > 785) {
                document.getElementsByTagName('body')?.[0]?.classList.remove('fixed');
            }
            if (props.OpenSCFModalOnLoad) {
                onDistanceLocationModalToggle();
            }

            setIsMounted(true);
        });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', cookieBannerResize);
        };
    }, []);
    useEffect(function runFacetedSearch() {
        if (isMounted && !showDistanceModal) {
            runSearch(false);
        }
    }, [showDistanceModal, searchData.SelectedSortValue]);

    useEffect(function runPageSearch() {
        if (isMounted) {
            runSearch(true);
        }
    }, [searchData.CurrentPage]);

    useEffect(() => {
        if (locationState && currentSite?.Site?.IsCharterHub) {
            runSearch(false, {
                fieldName: constants.Bounds,
                value: locationState
            });
        }
    }, [locationState]);


    const buildUrl = (keepPage, pendingChanges, searchField) => {
        let searchUrl = dealerDirectorySearchURL;
        if (pendingChanges) {
            pendingChanges.forEach(field => {
                if (field.fieldName == queryString.InventoryType) {
                    searchUrl = removeURLParameter(searchUrl, queryString.Category);
                    searchUrl = removeURLParameter(searchUrl, queryString.Manufacturer);
                    searchUrl = removeURLParameter(searchUrl, queryString.FTManufacturer);
                    searchUrl = removeURLParameter(searchUrl, queryString.FTCategory);
                }
                if ((field.fieldName == queryString.Category || field.fieldName == queryString.Manufacturer) && field.value && !searchUrl.includes(queryString.InventoryType) && props.HasDealerDirectorySegmentFacet) {
                    searchUrl = insertURLParameter(searchUrl, queryString.InventoryType, queryString.Equipment);
                }
                searchUrl = removeURLParameter(searchUrl, field.fieldName);

                if (field.fieldName == constants.Distance) {
                    searchUrl = removeURLParameter(searchUrl, constants.Distance);
                    searchUrl = removeURLParameter(searchUrl, constants.CityID);
                    searchUrl = removeURLParameter(searchUrl, constants.AirportID);

                    if (field.airport?.AirportCodeID > 0) {
                        searchUrl = insertURLParameter(searchUrl, constants.AirportID, field.airport.AirportCodeID);
                    }
                    if (field.airport?.AirportCityID > 0) {
                        searchUrl = insertURLParameter(searchUrl, constants.CityID, field.airport.AirportCityID);
                    }
                    if (field.distance && field.distance > 0) {
                        searchUrl = insertURLParameter(searchUrl, constants.Distance, field.distance);
                    }

                    if (!isNaN(field.airport.lat) && !isNaN(field.airport.lng)) {
                        handleSetMapCenter(field.airport.lat, field.airport.lng, field.distance);
                    }
                }

                else if (field.fieldName == constants.Bounds) {
                    if (field.value) {
                        Object.keys(field.value).forEach(key => {
                            searchUrl = removeURLParameter(searchUrl, key);
                            searchUrl = insertURLParameter(searchUrl, key, field.value[key]);
                        });
                    }
                }

                else if (field.value) {
                    searchUrl = insertURLParameter(searchUrl, field.fieldName, field.value);
                }
            });
        }

        if (searchData.SelectedSortValue == 6) {
            searchUrl = insertURLParameter(searchUrl, constants.sort, searchData.SelectedSortValue);
        } else {
            searchUrl = removeURLParameter(searchUrl, constants.sort);
        }

        if (searchField) {
            searchUrl = insertURLParameter(searchUrl, 'SearchProperty', searchField);
        }

        searchUrl = removeURLParameter(searchUrl, 'Page');
        if (keepPage === true) {
            searchUrl = insertURLParameter(searchUrl, 'Page', searchData.CurrentPage);
        }

        searchUrl = removeURLParameter(searchUrl, constants.UseCurrentLocation);
        if (useCurrentLocation) {
            searchUrl = insertURLParameter(searchUrl, constants.UseCurrentLocation, useCurrentLocation)
        }

        return searchUrl;
    };

    const updatePendingChangesForSearch = (pendingChanges, latestFieldValue) => {
        let newField = true;
        for (var i = 0; i < pendingChanges.length; i++) {
            if (latestFieldValue.fieldName == pendingChanges[i].fieldName) {
                pendingChanges[i].value = latestFieldValue.value;
                newField = false;
                break;
            }
        }

        if (newField) {
            pendingChanges.push(latestFieldValue);
        }

        let fields = [...searchData.SearchFields];
        for (var j = 0; j < fields.length; j++) {
            let field = fields[j];
            if (latestFieldValue.fieldName == field.FieldName && field.Type == 'Range') {
                // Update the range field in state so it doesn't do a weird re-render with the old value:
                let splitValue = latestFieldValue.value.split('*');
                field.SelectedLowerBound = splitValue[0];
                field.SelectedUpperBound = splitValue[1];
                setSearchData(previousState => {
                    return { ...previousState, SearchFields: fields };
                });
                break;
            }
        }

        return pendingChanges;
    };

    const runSearch = (keepPage, latestFieldValue) => {
        let pendingChanges = [...outstandingChanges];
        if (latestFieldValue) {
            if (Array.isArray(latestFieldValue)) {
                for (var i = 0; i < latestFieldValue.length; i++) {
                    pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue[i]);
                }
            } else {
                pendingChanges = updatePendingChangesForSearch(pendingChanges, latestFieldValue);
            }
        }

        let searchField = undefined;
        if (event && event.target) {
            searchField = event.target.name;
        }

        if (searchField == 'InventoryType') {
            let manuFacet = searchData.SearchFields.filter(field => field.FieldName == 'Manufacturer')[0];
            let catFacet = searchData.SearchFields.filter(field => field.FieldName == 'Category')[0];
            let ftManuFacet = searchData.SearchFields.filter(field => field.FieldName == 'ftManufacturer')[0];
            let ftCatFacet = searchData.SearchFields.filter(field => field.FieldName == 'FTCategory')[0];

            if (manuFacet) {
                manuFacet.Options.map(option => option.Checked = false);
            }

            if (catFacet) {
                catFacet.Options.map(option => option.Checked = false);
            }

            if (ftManuFacet) {
                ftManuFacet.Options.map(option => option.Checked = false);
            }

            if (ftCatFacet) {
                ftCatFacet.Options.map(option => option.Checked = false);
            }

            let inventoryType = searchData.SearchFields.filter(field => field.FieldName == 'InventoryType')[0];
            if (inventoryType.Options.filter(option => option.Checked).length == 1 && inventoryType.Options.filter(option => option.Checked)[0].Value == 'Parts') {
                let stateFacet = searchData.SearchFields.filter(field => field.FieldName == 'State')[0];
                let countryFacet = searchData.SearchFields.filter(field => field.FieldName == 'Country')[0];
                if (stateFacet) {
                    stateFacet.Options.map(option => option.Checked = false);
                }

                if (countryFacet) {
                    countryFacet.Options.map(option => option.Checked = false);
                }
            }
        } else if (searchField == 'Manufacturer' || searchField == 'Category') {
            let inventoryType = searchData.SearchFields.filter(field => field.FieldName == 'InventoryType')[0];
            if (inventoryType && inventoryType.Options && inventoryType.Options.filter(option => option.Checked).length > 1) {
                inventoryType.Options.filter(option => option.Value != 'Equipment').map(option => option.Checked = false);
            }
        }

        if (windowSize.width <= 785) {
            setScrollTop(0);
        }

        performSearch(buildUrl(keepPage, pendingChanges, searchField));
    };

    const performSearch = (newSearchURL) => {
        setSearchInProgress(true);
        setOutstandingChanges([]);
        setDealerDirectorySearchURL(newSearchURL);
        sandhillsRequest({
            url: newSearchURL,
            method: 'post',
        }).then((data) => {
            handleResponse(data);
        });
    };

    const handleResponse = (result) => {
        setSearchData({
            SearchBaseURL: result.DealerDirectoryURL,
            Dealers: result.Dealers,
            SearchFields: result.SearchFields,
            ListingsCount: result.PaginationModel.TotalListingsCountFormatted,
            PageHeader: result.PageHeader,
            CurrentPage: result.PaginationModel.CurrentPage,
            TotalPages: result.PaginationModel.TotalPages,
            InternalLinkingModel: result.InternalLinkingModel,
            SelectedSortValue: result.SelectedSortValue,
            IsDefaultSearch: result.IsDefaultSearch,
            CategoryInfo: result.CategoryInformation,
            LocationString: result.LocationString,
            ListingLocations: result.ListingLocations,
            ListingMapData: result.ListingMapData,
            DefaultZoom: result.DefaultZoom,
            DefaultCenter: result.DefaultCenter,
            FlightPaths: result.FlightPaths
        });

        setDealerDirectorySearchURL(result.DealerDirectorySearchURL);
        setSearchInProgress(false);
        document.title = result.PageTitle;
        window.history.pushState('', '', result.DealerDirectoryURL);
        syncCanonicalUrl(result.DealerDirectoryURL);
        setShowLoadingRing(false);
    };

    const toggleMobileSort = () => {
        togglePushNotifications(showMobileSort);
        toggleScrollToTop(showMobileSort);
        setShowMobileSort(!showMobileSort);
    };

    const handleScroll = () => {
        if (showMobileSort) {
            setShowMobileSort(false);
        }
    };

    const handlePageNumberChange = (page) => {
        setShowLoadingRing(true);
        setSearchData(previousState => {
            return { ...previousState, CurrentPage: page };
        });
        setScrollTop(0);
    };

    const handleSetMapCenter = (lat, lng, distance) => {
        if (locationMapRef.current) {
            locationMapRef.current.setMapCenter(lat, lng, distance);
        }
    }

    const mapViewToggle = () => {
        setShowMapViewModal(showMapViewModal => !showMapViewModal);
    };

    const toggleSearchWindow = () => {
        if (windowSize.isMobile) {
            if (showSearchWindow) {
                removeIOSScrollLock(true);
            } else {
                setIOSScrollLock(true);
            }
        }
        let showWindow = !showSearchWindow;
        if (showWindow) {
            document.getElementsByTagName('body')?.[0]?.classList.add('fixed');
        } else {
            document.getElementsByTagName('body')?.[0]?.classList.remove('fixed');
        }

        setShowSearchWindow(showWindow);
        setMobileSearch(showWindow && window.innerWidth <= 785)
    };

    const clearAll = () => {
        setScrollTop(0);
        setSearchInProgress(true);
        setDealerDirectorySearchURL(props.DealerDirectorySearchURL.split('?')[0]);
        setSearchData(previousState => {
            return { ...previousState, SelectedSortValue: 1 };
        });

        sandhillsRequest({
            url: props.DealerDirectorySearchURL.split('?')[0],
            method: 'post'
        }).then((data) => {
            handleResponse(data)
        });

        window.history.pushState('', '', searchData.SearchBaseURL.split('?')[0]);
        syncCanonicalUrl(searchData.SearchBaseURL.split('?')[0]);
    };

    const clearFacet = (fieldName, option) => {
        let searchUrl = buildUrl(false);
        //copy field name so we don't mutate 
        let queryStringParamName = (' ' + fieldName).slice(1);
        let selectedField = searchData.SearchFields.filter(f => f.FieldName == fieldName)[0];

        if (option && option.IsGroup && selectedField?.ParentNameForSearch) {
            queryStringParamName = selectedField.ParentNameForSearch;
        }

        if (fieldName == constants.Distance) {
            searchUrl = removeURLParameter(searchUrl, constants.Distance);
            searchUrl = removeURLParameter(searchUrl, constants.CityID);
            searchUrl = removeURLParameter(searchUrl, constants.AirportID);

            //reset default map
            handleSetMapCenter(40.8058, -96.6718, 0);
        }

        if (option) {
            let qsValues = getURLParameter(searchUrl, queryStringParamName).split('|');
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);

            let filter = qsValues;
            let index = qsValues.indexOf(option.Value ? option.Value : option);
            if (index > -1) {
                filter.splice(index, 1);
            }

            if (filter.length > 0 && filter[0] !== "") {
                searchUrl = insertURLParameter(searchUrl, queryStringParamName, filter.join('|'));
            }
        } else {
            searchUrl = removeURLParameter(searchUrl, queryStringParamName);
        }

        if (queryStringParamName == queryString.InventoryType) {
            searchUrl = removeURLParameter(searchUrl, queryString.Category);
            searchUrl = removeURLParameter(searchUrl, queryString.Manufacturer);
            searchUrl = removeURLParameter(searchUrl, queryString.FTManufacturer);
            searchUrl = removeURLParameter(searchUrl, queryString.FTCategory);
        }

        performSearch(searchUrl);
    };

    const clearFacetDebounced = useMemo(() => debounce(clearFacet, 300), [clearFacet]);

    const onDistanceLocationModalToggle = () => {
        let newShowDistanceModal = !showDistanceModal;
        setShowDistanceModal(newShowDistanceModal);
    };

    const onDistanceLocationModalUpdate = (location) => {
        setOutstandingChanges(previousState => {
            return [
                ...previousState
            ];
        });
        setUseCurrentLocation(location?.UseCurrentLocation ?? false);
        setSearchData(previousState => {
            return { ...previousState, SelectedSortValue: 6 };
        });
        setShowDistanceModal(false);
    };

    const onSortChange = (sortID) => {
        setUseCurrentLocation(false);
        if (sortID == 6 && searchData.SelectedSortValue != 6) {
            getUserPosition(true, null, routes.AjaxSetCurrentUserLocationCookie, routes.AjaxGetDecryptedCoordinatesFromCurrentLocationCookie).then((currentPosition) => {
                if (currentPosition && currentPosition.latitude && currentPosition.longitude) {
                    setOutstandingChanges(previousState => {
                        return [
                            ...previousState,
                        ];
                    });
                    setSearchData(previousState => {
                        return { ...previousState, SelectedSortValue: sortID };
                    });
                } else {
                    onDistanceLocationModalToggle();
                }
            });
        } else {
            setSearchData(previousState => {
                return { ...previousState, SelectedSortValue: sortID };
            });
        }
    };

    const onEmailModalToggle = () => {
        setShowEmailModal(showEmailModal => !showEmailModal);
        if (returnFocusRef && returnFocusRef.current) {
            returnFocusRef.current.focus();
        }
    };

    const openEmailModal = (inquiryType, modalData) => {
        modalData = modalData || {};
        setSelectedInquiry(inquiryType);
        setShowEmailModal(showEmailModal => !showEmailModal);
        setSamModel({ ...samModel, ...modalData });
        setReturnFocusRef(modalData.ref);
    };

    return (
        <div className='dealer-directory'>
            {showLoadingRing && <LoadingRing sxBox={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999, }} />}
            {showDistanceModal &&
                <DistanceLocationModal
                    OnUpdate={onDistanceLocationModalUpdate}
                    ToggleDistanceModal={onDistanceLocationModalToggle}
                    LocationAutosuggestUrl={routes.LocationAutosuggestUrl}
                    AjaxGetCoordinatesFromLocationStringUrl={routes.AjaxGetCoordinatesFromLocationStringUrl}
                    AjaxFormattedLocationStringFromCurrentCoordinatesUrl={routes.FormattedLocationStringFromCurrentCoordinates}
                    CurrentAuthenticatedUserLocationForCoordinates={currentUser.CurrentLocation?.CurrentAuthenticatedUserLocationForCoordinates}
                    CurrentUserFormattedAddress={searchData.LocationString}
                    DisplayShowClosestFirstByConsent={props.DisplayShowClosestFirstByConsent}
                />}
            <section className='list-top-section'>
                <CategoryInformation {...searchData.CategoryInfo} />
                <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={searchData.PageHeader} />
                <div className='page-title'>
                    <h1 className='dealer-title'>
                        <strong><span>{searchData.PageHeader}{props.FBOVendorInformationURL && <><br /><a className='fbo-vendor-information-link' href={props.FBOVendorInformationURL}>Update FBO Information</a></>}</span></strong>
                    </h1>
                    <span className='match-count'><TranslationText tKey='sandhills.tradepubwebsite.mvc.numbermatches' /> : {searchData.ListingsCount}</span>
                    {props.SortOrderItems && props.SortOrderItems.length > 0 &&
                        <div className='list-sort-container'>
                            {props.SortOrderItems && props.SortOrderItems.length > 0 &&
                                <SortDropdown
                                    ShowMobileSort={false}
                                    IsDesktop={!mobileSearch}
                                    SortOptions={props.SortOrderItems}
                                    OnChange={onSortChange}
                                    SelectedValue={searchData.SelectedSortValue}
                                    setShowDistanceModal={setShowDistanceModal}
                                />}
                            {searchData.SelectedSortValue == 6 &&
                                <div className='distance-sort-descriptor' >
                                    <p className='distance-from'>
                                        <TranslationText tKey='sandhills.distancefromterm' replaceValues={['']} />
                                        <span className='distance-nearest-sort'>{searchData.LocationString}</span>
                                        <a className='distance-sort-update' role="button" tabindex="0" onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onDistanceLocationModalToggle();
                                            }
                                        }} onClick={onDistanceLocationModalToggle} aria-label="Update Location">
                                            <TranslationText tKey='sandhills.update' />
                                        </a>
                                    </p>
                                </div>}
                        </div>}
                </div>
            </section>
            <section className='bottom-section'>
                <div className='side-container'>
                    <FacetedSearch
                        {...searchData}
                        runSearch={runSearch}
                        clearAll={clearAll}
                        clearFacet={clearFacetDebounced}
                        disabled={searchInProgress}
                        showMobileFilters={showSearchWindow}
                        toggleMobileFilter={toggleSearchWindow}
                        mobileSearch={mobileSearch}
                        UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                        setShowLoadingRing={setShowLoadingRing}
                    />
                </div>
                <div className='map-listings-container'>
                    {!windowSize.isMobile && currentSite?.Site?.IsCharterHub &&
                        <>
                        <LocationMap
                            locations={searchData.ListingLocations}
                            listingMapData={searchData.ListingMapData}
                            onLocationChange={setLocationState}
                            defaultCenter={searchData.DefaultCenter}
                            defaultZoom={searchData.DefaultZoom}
                            disableAutoCentering={!searchData.DefaultCenter}
                            ref={locationMapRef}
                            flightPaths={searchData.FlightPaths}
                            isOpen={!props.IsFBODirectory}
                            currentUser={currentUser}
                        />
                            {!props.IsFBODirectory && (
                                <h2><TranslationText tKey='sandhills.tradesites.mvc.explorechartercompanies' /></h2>
                            )}
                        </>
                    }
                    <div id='main-content' className='dealer-directory-content'>
                        {props.IsFBODirectory ?
                            (
                                <div className='fbo-listings-wrapper'>
                                    {searchData.Dealers && searchData.Dealers.length > 0 ?
                                        searchData.Dealers.map((item, index) =>
                                            <FBODirectoryListing
                                                {...item}
                                                key={item.LocationID}
                                                FormattedLocation={searchData.LocationString}
                                                IsCoopDirectory={props.IsCoopDirectory}
                                                openEmailModal={openEmailModal}
                                                ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                                                UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                                                IsFirst={index === 0}
                                            />
                                        )
                                        :
                                        <div className='no-dealers-filter'>
                                            <div><TranslationText tKey='sandhills.tradesites.mvc.sorryyouhavefilteredoutalldealers' /></div>
                                            <div>
                                                <TranslationText tKey='sandhills.tradesites.mvc.adjustyourfiltersorperformanewsearchbyselecting' /> '
                                                <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); clearAll(); } }} className='clear-all-text' onClick={clearAll}><TranslationText tKey='sandhills.clearall' /></a>
                                                ' <TranslationText tKey='sandhills.tradesites.mvc.toseemoredealers' />
                                            </div>
                                        </div>
                                    }
                                </div>
                            )
                            :
                            (
                                <div className='dealer-listings-wrapper'>
                                    {searchData.Dealers && searchData.Dealers.length > 0 ? searchData.Dealers.map((item, index) =>
                                        <DealerDirectoryListing
                                            {...item}
                                            key={item.LocationID}
                                            FormattedLocation={searchData.LocationString}
                                            IsCoopDirectory={props.IsCoopDirectory}
                                            IsEmptyLegsDirectory={props.IsEmptyLegsDirectory}
                                            openEmailModal={openEmailModal}
                                            ShowEmployeeOnlyInfo={props.ShowEmployeeOnlyInfo}
                                            UseSEOHTagReorganization={props.UseSEOHTagReorganization}
                                            IsFirst={index === 0}
                                        />
                                    ) :
                                        <div className='no-dealers-filter'>
                                            <div><TranslationText tKey='sandhills.tradesites.mvc.sorryyouhavefilteredoutalldealers' /></div>
                                            <div>
                                                <TranslationText tKey='sandhills.tradesites.mvc.adjustyourfiltersorperformanewsearchbyselecting' /> '
                                                <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); clearAll(); } }} className='clear-all-text' onClick={clearAll}><TranslationText tKey='sandhills.clearall' /></a>
                                                ' <TranslationText tKey='sandhills.tradesites.mvc.toseemoredealers' />
                                            </div>
                                        </div>
                                    }
                                </div>
                            )
                        }
                        {searchData.TotalPages > 1 &&
                            <Paging
                                currentPage={searchData.CurrentPage}
                                totalPages={searchData.TotalPages}
                                onChange={handlePageNumberChange}
                            />}
                    </div>
                </div>
                <div className='clear' />
                <div>
                    {searchData.InternalLinkingModel && searchData.InternalLinkingModel.InternalLinkGroups &&
                        <SEOLinks
                            {...searchData.InternalLinkingModel}
                            SearchURL={dealerDirectorySearchURL}
                        />}
                </div>
            </section>
            <FacetedSearchMobileMenu
                filter={{
                    render: true,
                    toggle: toggleSearchWindow
                }}
                sort={{
                    render: showMobileSort,
                    toggle: toggleMobileSort,
                    change: onSortChange,
                    options: props.SortOrderItems,
                    value: searchData.SelectedSortValue
                }}
                flyout={{
                    render: props.UseClassicViewFlyout,
                    url: props.ClassicFlyoutModel.ClassicViewURL,
                    img: props.ClassicFlyoutModel.ClassicViewImageURL
                }}
                setShowDistanceModal={setShowDistanceModal}
                setShowLoadingRing={setShowLoadingRing}
            />
            {searchInProgress && <div className='searching-background' />}
            {showEmailModal &&
                <EmailModal
                    {...samModel}
                    toggleEmailModal={onEmailModalToggle}
                    selectedInquiry={selectedInquiry}
                    IsFBO={props.IsFBODirectory}
                    DefaultCultureCode={props.DefaultCultureCode}
                />}
            {currentSite.IsMobileAppContext &&
                <div className='detail__mobile-padding-bottom' />
            }
        </div>
    );
};

export default DealerDirectoryView;
global.DealerDirectoryView = DealerDirectoryView;